import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import type { SupportedLanguage } from '@/i18n/config';
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from '@/i18n/config';
import i18n from '@/i18n/config';
import { apiCache } from '@/utils/cache/apiCache';
import { useBrand } from '@/contexts/BrandContext';
import type { TFunction } from 'i18next';

interface LanguageContextType {
  currentLanguage: SupportedLanguage;
  supportedLanguages: typeof SUPPORTED_LANGUAGES;
  changeLanguage: (lang: SupportedLanguage) => Promise<void>;
  isEnglish: boolean;
  t: TFunction; // 直接从全局 i18n 获取，绕过 useTranslation
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const brand = useBrand();
  // 语言优先级：用户手动选择（localStorage） > 品牌站点默认语种 > 全局默认（zh-CN）
  // 例：juxflow.ame-film.com 配置 defaultLanguage='en-US'，未手动切换过时默认英文
  const [currentLanguage, setCurrentLanguage] = useState<SupportedLanguage>(
    () =>
      (localStorage.getItem('i18n_language') as SupportedLanguage) ||
      brand.defaultLanguage ||
      DEFAULT_LANGUAGE
  );

  // 用户未手动切换过时，把品牌默认语种同步给 i18next（i18next 初始化时只认 localStorage / 全局默认）
  useEffect(() => {
    const saved = localStorage.getItem('i18n_language');
    if (!saved && brand.defaultLanguage && i18n.language !== brand.defaultLanguage) {
      i18n.changeLanguage(brand.defaultLanguage);
    }
  }, [brand.defaultLanguage]);

  const changeLanguage = useCallback(
    async (lang: SupportedLanguage) => {
      localStorage.setItem('i18n_language', lang);
      // 首页列表（prompt 等字段由后端按 Accept-Language 本地化）随语种变化，
      // 切换时清掉旧语种缓存，避免重新请求命中旧数据
      apiCache.deleteByPrefix('/home');
      apiCache.deleteByPrefix('/model/list');
      await i18n.changeLanguage(lang);

      setCurrentLanguage(lang);
    },
    []
  );

  // 监听 i18next 语言变化
  useEffect(() => {
    const handleLanguageChanged = (lng: string) => {
      if (lng === 'zh-CN' || lng === 'en-US') {
        setCurrentLanguage(lng);
      }
    };

    i18n.on('languageChanged', handleLanguageChanged);
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, []);
  // 根据当前语言同步页面 title；film.wjark / juxflow 等独立品牌展示品牌标题（支持按语种），其他走 i18n
  useEffect(() => {
      const localizedTitle =
        brand.localizedTitles?.[currentLanguage as 'zh-CN' | 'en-US'];
      document.title =
        brand.subdomain === 'film.wjark' || brand.key === 'juxflow'
          ? (localizedTitle ?? brand.title)
          : i18n.t('pageTitle');
  }, [currentLanguage, brand.title, brand.localizedTitles, brand.subdomain, brand.key]);


  const value: LanguageContextType = useMemo(() => ({

    currentLanguage,
    supportedLanguages: SUPPORTED_LANGUAGES,
    changeLanguage,
    isEnglish: currentLanguage === 'en-US',
    t: i18n.t.bind(i18n), // 直接绑定全局 i18n 的 t 函数
  }), [currentLanguage, changeLanguage]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage 必须在 LanguageProvider 内使用');
  }
  return context;
};
